// The palette's spec search: names and references of the current workspace's cached list.

import type { ObjectView } from "@cosmosai/api-client";
import { parseRef } from "@cosmosai/api-client";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useCosmos } from "../../api/cosmos";
import { specsQuery } from "../../api/registry-queries";
import type { Command, CommandProvider } from "../../app/registry/commands";
import { presenterOf } from "../../kinds";
import { routes, useCosmosContext } from "../context";
import { matching } from "./match";

let index: { workspace: string; specs: ObjectView[] } | null = null;

/** Mounted in the shell: keeps the workspace's specs list at hand for the palette. */
export function SpecsIndex() {
  const { workspace } = useCosmosContext();
  const client = useCosmos();
  const { data } = useQuery({ ...specsQuery(client, workspace ?? ""), enabled: !!workspace });
  useEffect(() => {
    index = workspace && data ? { workspace, specs: data } : null;
  }, [workspace, data]);
  return null;
}

/** At most `limit` specs of the index matching the query, as palette commands. */
export function searchSpecs(query: string, limit = 8): Command[] {
  if (!index) return [];
  const { workspace, specs } = index;
  return matching(specs, query)
    .slice(0, limit)
    .map((spec) => ({
      id: `spec:${spec.ref}`,
      group: "palette.groups.specs",
      label: spec.name,
      hint: spec.ref,
      icon: presenterOf(spec.kind).icon,
      keywords: [spec.ref, spec.kind],
      to: routes.spec(workspace, parseRef(spec.ref)),
    }));
}

export const specsProvider: CommandProvider = {
  id: "specs",
  search: async (query) => searchSpecs(query),
};
